<script setup>
import { ref, onMounted, computed, watch, reactive } from 'vue'
import { useRoute } from 'vue-router'
import { useFormulario } from '@/composables/useFormulario'
import { useAuthStore } from '@/stores/auth'
import { useMetadataListasStore } from '@/stores/metadataListas'
import api from '@/services/api'
import FormularioCadastro from '@/components/base/formulario/FormularioCadastro.vue'
import CampoInput from '@/components/base/campos/CampoInput.vue'
import CampoBusca from '@/components/base/campos/CampoBusca.vue'
import CampoBuscaLista from '@/components/base/campos/CampoBuscaLista.vue'
import AuditLogViewer from '@/components/base/auditoria/AuditLogViewer.vue'
import { criarConfigBuscaOperacao } from '@/search-definitions/operacaoSearch'
import { criarConfigBuscaPerfilTributario } from '@/search-definitions/perfilTributarioSearch'
import { TP_MOVIMENTO } from '@/constants/TP_MOVIMENTO'
import { GEO_UF } from '@/constants/GEO_UF'
import { PARCEIRO_TIPO } from '@/constants/PARCEIRO_TIPO'
import { NV_MODULO_FISCAL } from '@/constants/NV_MODULO_FISCAL'

const props = defineProps({
  itemParaEditar: {
    type: Object,
    default: null
  }
})

const emit = defineEmits(['itemAtualizado', 'cancelar', 'show-notification'])

const route = useRoute()
const authStore = useAuthStore()
const metadataStore = useMetadataListasStore()

const podeEditar = computed(() => {
  return authStore.isAdminInterno || (authStore.permissoes && authStore.permissoes.includes('FISCAL_MATRIZ_COMPLETO'))
})

const nivelFiscal = ref('CARREGANDO')
const nivelFiscalDescricao = ref('A Carregar...')

const carregarNivelFiscal = async () => {
  let nivelCodigo = NV_MODULO_FISCAL.Nenhum
  nivelFiscalDescricao.value = 'A Carregar...'

  try {
    if (authStore.isAdminInterno) {
      if (authStore.empresaAtiva) {
        const resEmpresa = await api.get(`/Empresas/${authStore.empresaAtiva}`)
        const idPlano = resEmpresa.data.idPlanoVigente || resEmpresa.data.IdPlanoVigente

        if (idPlano) {
          const resPlano = await api.get(`/Planos/${idPlano}`)
          nivelCodigo = resPlano.data.lstNivelModuloFiscal || resPlano.data.LstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
        }
      }
    } else {
      nivelCodigo = authStore.lstNivelModuloFiscal || NV_MODULO_FISCAL.Nenhum
    }
  } catch (e) {
    // Silenciado
  }

  nivelFiscal.value = String(nivelCodigo).toUpperCase()

  try {
    if (nivelCodigo !== NV_MODULO_FISCAL.Nenhum) {
      const resLista = await api.get('/ListasSistema/lookup/NV_MODULO_FISCAL')
      const itemLista = resLista.data.find(i =>
        String(i.idListaSistemaItem).toUpperCase() === nivelFiscal.value ||
        String(i.textoLista).toUpperCase() === nivelFiscal.value
      )
      nivelFiscalDescricao.value = itemLista ? itemLista.textoLista : nivelFiscal.value
    } else {
      nivelFiscalDescricao.value = 'Nenhum'
    }
  } catch (e) {
    nivelFiscalDescricao.value = nivelFiscal.value
  }
}

// GUARDIÃO: O Segredo estava aqui!
// Declarar as propriedades de chave estrangeira no molde inicial impede que o Vue as expurgue no momento do submitForm.
const formDefaults = {
  idMatrizFiscal: null,
  idOrganizacao: null,
  idOperacao: null,
  idPerfilTributario: null,
  lstUfOrigem: null,
  lstUfDestino: null,
  codigoFiscal: '',
  lstTipoPessoaDestino: null,
  lstRegimeTributario: null,
  dataValidadeInicio: null,
  dataValidadeFim: null
}

const { item, isLoading, isEditing, entityInfo, submitForm, initializeForm, handleCancel } = useFormulario(props, emit, formDefaults)

const fkOperacao = ref(null)
const fkPerfil = ref(null)

const mostrarAuditoria = ref(false)

const confBuscaOperacao = computed(() => criarConfigBuscaOperacao(authStore.organizacaoAtiva))
const confBuscaPerfilTributario = computed(() => criarConfigBuscaPerfilTributario(authStore.organizacaoAtiva))

// ============================================================
// Listas dinâmicas via DicionarioMetadadosListas (Seção 16)
// ============================================================
const listas = reactive({})

const carregarListas = async () => {
  try {
    const result = await metadataStore.carregarListasParaTabelas(['MatrizFiscal'])
    Object.assign(listas, result)
  } catch (error) {
    console.error('Erro ao carregar listas da Matriz Fiscal:', error)
  }
}

// ============================================================
// Detalhes da Operação selecionada (para saber Entrada/Saída)
// ============================================================
const operacaoSelecionada = ref(null)

watch(fkOperacao, async (novoId) => {
  if (!novoId) {
    operacaoSelecionada.value = null
    return
  }
  try {
    const res = await api.get(`/Operacoes/${novoId}`)
    operacaoSelecionada.value = res.data
  } catch {
    operacaoSelecionada.value = null
  }
})

watch(() => props.itemParaEditar, (newVal) => {
  if (newVal) {
    fkOperacao.value = newVal.idOperacao
    fkPerfil.value = newVal.idPerfilTributario
  } else {
    fkOperacao.value = null
    fkPerfil.value = null
  }
}, { immediate: true })

// ============================================================
// Regra de Definição de EX para Origem/Destino
// ============================================================
const isEstrangeiro = computed(() => {
  return item.value.lstTipoPessoaDestino === PARCEIRO_TIPO.Estrangeiro
})

// Watch combinado: TipoPessoa + Operação → limpa campos e define EX se estrangeiro
// GUARDIÃO: Usar os valores antigos do watch para distinguir inicialização do formulário
// (que deve preservar os UFs carregados do backend) de interação do usuário (que deve limpar).
watch([() => item.value.lstTipoPessoaDestino, operacaoSelecionada], ([, novaOperacao], [tipoAntigo, operacaoAntiga]) => {
  // Invocação 1 da inicialização: tipoPessoa está sendo carregada do backend
  // (tipoAntigo === undefined porque item.value era um objeto vazio {} )
  const isInitTipoPessoa = tipoAntigo === undefined && !novaOperacao
  // Invocação 2 da inicialização: operacao está sendo carregada pela primeira vez
  // (operacaoAntiga === null porque operacaoSelecionada inicia como null)
  const isInitOperacao = operacaoAntiga === null && novaOperacao && item.value.lstUfOrigem
  if (isInitTipoPessoa || isInitOperacao) return

  // A partir daqui é mudança do usuário → limpar e recalcular
  item.value.lstUfOrigem = null
  item.value.lstUfDestino = null

  const tipoMov = operacaoSelecionada.value?.lstTipoMovimento
  if (!tipoMov || !isEstrangeiro.value) return

  if (tipoMov === TP_MOVIMENTO.Entrada) {
    // Entrada + Estrangeiro → Origem = EX, Destino livre
    item.value.lstUfOrigem = GEO_UF.EX
  } else if (tipoMov === TP_MOVIMENTO.Saida) {
    // Saída + Estrangeiro → Destino = EX, Origem livre
    item.value.lstUfDestino = GEO_UF.EX
  }
})

const ufOrigemBloqueada = computed(() => {
  if (!podeEditar.value) return true
  if (!operacaoSelecionada.value || !isEstrangeiro.value) return false
  return operacaoSelecionada.value.lstTipoMovimento === TP_MOVIMENTO.Entrada
})

const ufDestinoBloqueada = computed(() => {
  if (!podeEditar.value) return true
  if (!operacaoSelecionada.value || !isEstrangeiro.value) return false
  return operacaoSelecionada.value.lstTipoMovimento === TP_MOVIMENTO.Saida
})

async function salvar () {
  if (!podeEditar.value) return

  const payload = {
    ...item.value,
    // GUARDIÃO: Escopo multiorganização (Tenant) — injeta a organização ativa.
    // O backend faz override absoluto via ITenantContext; o envio aqui é defesa em profundidade.
    idOrganizacao: authStore.organizacaoAtiva ? Number(authStore.organizacaoAtiva) : null,
    idOperacao: fkOperacao.value,
    idPerfilTributario: fkPerfil.value
  }

  const idDaRota = route.params.id ? Number(route.params.id) : null
  const idDaProp = props.itemParaEditar?.idMatrizFiscal || props.itemParaEditar?.id || null
  const idDoItem = item.value?.idMatrizFiscal || item.value?.id || null
  const idReal = idDoItem || idDaProp || idDaRota

  payload.idMatrizFiscal = idReal
  payload.id = idReal

  // Sanitização de payload para o novo campo universal (codigoFiscal)
  if (!payload.codigoFiscal || payload.codigoFiscal.trim() === '') payload.codigoFiscal = null
  if (!payload.lstTipoPessoaDestino) payload.lstTipoPessoaDestino = null
  if (!payload.lstRegimeTributario) payload.lstRegimeTributario = null

  await submitForm('MatrizesFiscais', 'idMatrizFiscal', payload)
}

async function salvarAlteracoes () {
  if (!podeEditar.value) return

  const payload = {
    ...item.value,
    // GUARDIÃO: Escopo multiorganização (Tenant) — injeta a organização ativa.
    // O backend faz override absoluto via ITenantContext; o envio aqui é defesa em profundidade.
    idOrganizacao: authStore.organizacaoAtiva ? Number(authStore.organizacaoAtiva) : null,
    idOperacao: fkOperacao.value,
    idPerfilTributario: fkPerfil.value
  }

  const idDaRota = route.params.id ? Number(route.params.id) : null
  const idDaProp = props.itemParaEditar?.idMatrizFiscal || props.itemParaEditar?.id || null
  const idDoItem = item.value?.idMatrizFiscal || item.value?.id || null
  const idReal = idDoItem || idDaProp || idDaRota

  payload.idMatrizFiscal = idReal
  payload.id = idReal

  if (!payload.codigoFiscal || payload.codigoFiscal.trim() === '') payload.codigoFiscal = null
  if (!payload.lstTipoPessoaDestino) payload.lstTipoPessoaDestino = null
  if (!payload.lstRegimeTributario) payload.lstRegimeTributario = null

  try {
    isLoading.value = true
    const res = idReal
      ? await api.put(`/MatrizesFiscais/${idReal}`, payload)
      : await api.post('/MatrizesFiscais', payload)

    if (res?.data) {
      initializeForm(res.data)
    }
    emit('show-notification', { message: 'Alterações salvas com sucesso!', type: 'success' })
  } catch (error) {
    let msg = 'Erro ao salvar alterações.'
    if (error.response?.data?.errors) {
      msg = Object.values(error.response.data.errors).flat()[0] || msg
    } else if (error.response?.data?.message || error.response?.data?.title) {
      msg = error.response.data.message || error.response.data.title
    } else if (typeof error.response?.data === 'string') {
      msg = error.response.data
    }
    emit('show-notification', { message: msg, type: 'error' })
  } finally {
    isLoading.value = false
  }
}

watch(() => authStore.empresaAtiva, () => {
  carregarNivelFiscal()
  if (authStore.empresaAtiva) {
    carregarListas()
  }
})

onMounted(() => {
  carregarNivelFiscal()
  if (authStore.empresaAtiva) {
    carregarListas()
  }
})
</script>

<template>
  <FormularioCadastro
    module-name="Regra da Matriz Fiscal"
    :is-loading="isLoading"
    :is-editing="isEditing"
    :entity-info="entityInfo"
    @submit="salvar"
    @submit-continuar="salvarAlteracoes"
    @cancelar="handleCancel"
    @show-notification="(p) => emit('show-notification', p)"
    :ocultar-salvar="!podeEditar"
  >
    <template #acoes-header>
      <span class="badge bg-dark border border-secondary-subtle px-3 py-2 me-3 d-flex align-items-center" style="font-size: 0.85rem;">
        <i class="bi bi-shield-check me-2 text-info"></i> Nível Fiscal: {{ nivelFiscalDescricao }}
      </span>

      <button
        v-if="isEditing"
        type="button"
        class="btn-icon-minimalista me-3"
        title="Ver Histórico de Alterações"
        @click="mostrarAuditoria = true"
      >
        <i class="bi bi-clock-history"></i>
      </button>
    </template>

    <div v-if="!podeEditar" class="alert alert-warning border-0 rounded-3 mb-4 d-flex align-items-center">
      <i class="bi bi-lock-fill fs-4 me-3 text-warning"></i>
      <div><strong>Modo de Leitura:</strong> O seu perfil não tem permissões para editar este registo.</div>
    </div>

    <!-- Frame 1: Contexto da Negociação -->
    <div class="card mb-4 shadow-sm border-0 border-start border-4 border-primary">
      <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
        <h6 class="mb-0 text-body-emphasis"><i class="bi bi-handshake me-2"></i>Contexto da Negociação</h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-4">
            <CampoBusca
              id="idOperacao"
              label="Operação Fiscal"
              v-model="fkOperacao"
              :configuracao-busca="confBuscaOperacao"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-4">
            <CampoBuscaLista
              id="lstTipoPessoaDestino"
              label="Tipo de Pessoa do Parceiro"
              v-model="item.lstTipoPessoaDestino"
              tabela="MatrizFiscal"
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-4">
            <CampoBuscaLista
              id="lstRegimeTributario"
              label="Regime do Parceiro"
              v-model="item.lstRegimeTributario"
              tabela="MatrizFiscal"
              :disabled="!podeEditar"
            />
          </div>
        </div>
      </div>
    </div>

    <!-- Frame 2: Localidade -->
    <div class="card mb-4 shadow-sm border-0 border-start border-4 border-info">
      <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
        <h6 class="mb-0 text-body-emphasis"><i class="bi bi-geo-alt me-2"></i>Localidade (Origem & Destino)</h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-2">
            <CampoBuscaLista
              id="lstUfOrigem"
              label="UF de Origem"
              v-model="item.lstUfOrigem"
              tabela="MatrizFiscal"
              required
              :disabled="ufOrigemBloqueada"
            />
            <small v-if="ufOrigemBloqueada" class="text-info mt-1 d-block fw-medium">
              <i class="bi bi-lock-fill"></i> Fixado para Exterior (Entrada + Estrangeiro).
            </small>
          </div>
          <div class="col-md-2">
            <CampoBuscaLista
              id="lstUfDestino"
              label="UF de Destino"
              v-model="item.lstUfDestino"
              tabela="MatrizFiscal"
              required
              :disabled="ufDestinoBloqueada"
            />
            <small v-if="ufDestinoBloqueada" class="text-info mt-1 d-block fw-medium">
              <i class="bi bi-lock-fill"></i> Fixado para Exterior (Saída + Estrangeiro).
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Frame 3: Especificidade do Produto/Serviço -->
    <div class="card mb-4 shadow-sm border-0 border-start border-4 border-warning">
      <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3 d-flex justify-content-between align-items-center">
        <h6 class="mb-0 text-body-emphasis"><i class="bi bi-box-seam me-2"></i>Especificidade do Item (Refinamento)</h6>
        <i class="bi bi-info-circle text-muted" title="Deixe em branco para criar uma regra Curinga (válida para qualquer item). O sistema dará prioridade às regras mais específicas."></i>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-12">
            <CampoInput
              id="codigoFiscal"
              label="NCM / NBS / Código LC116"
              v-model="item.codigoFiscal"
              placeholder="Ex: 8517, 10.11 ou 1.01.01.01.00 (Branco = Todos os Itens)"
              type="text"
              maxlength="20"
              :disabled="!podeEditar"
            />
            <small class="text-muted mt-1 d-block">
              Deixe em branco para criar uma regra <strong>Curinga</strong> (válida para qualquer mercadoria ou serviço). O sistema dará prioridade às regras mais específicas.
            </small>
          </div>
        </div>
      </div>
    </div>

    <!-- Frame 4: Resultado Final -->
    <div class="card mb-4 shadow-sm border-0 border-start border-4 border-success">
      <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
        <h6 class="mb-0 text-body-emphasis text-success"><i class="bi bi-bullseye me-2"></i>Resultado Final</h6>
      </div>
      <div class="card-body">
        <div class="row align-items-center">
          <div class="col-md-8">
            <CampoBusca
              id="idPerfilTributario"
              label="Perfil Tributário (A Aplicar)"
              v-model="fkPerfil"
              :configuracao-busca="confBuscaPerfilTributario"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-4 text-end text-muted small mt-3 mt-md-0">
            <i class="bi bi-check2-circle text-success fs-3 d-block mb-1"></i>
            Esta é a carga tributária que o ERP utilizará na nota/pedido.
          </div>
        </div>
      </div>
    </div>

    <!-- Frame 5: Vigência da Regra -->
    <div class="card shadow-sm border-0 border-start border-4 border-secondary">
      <div class="card-header bg-transparent border-bottom-0 pb-0 pt-3">
        <h6 class="mb-0 text-body-emphasis"><i class="bi bi-calendar-range me-2"></i>Vigência da Regra</h6>
      </div>
      <div class="card-body">
        <div class="row g-3">
          <div class="col-md-2">
            <CampoInput
              id="dataValidadeInicio"
              label="Data de Início"
              v-model="item.dataValidadeInicio"
              type="date"
              required
              :disabled="!podeEditar"
            />
          </div>
          <div class="col-md-2">
            <CampoInput
              id="dataValidadeFim"
              label="Data de Término"
              v-model="item.dataValidadeFim"
              type="date"
              placeholder="Opcional (Em branco = Indeterminado)"
              :disabled="!podeEditar"
            />
          </div>
        </div>
      </div>
    </div>
  </FormularioCadastro>

  <AuditLogViewer
    v-if="mostrarAuditoria"
    tabela="MatrizFiscal"
    contexto="tenant"
    :chave-primaria="item.idMatrizFiscal"
    :titulo-registro="item.descricao"
    @fechar="mostrarAuditoria = false"
  />
</template>
